import {
    CallMediaSession,
    type ActiveMediaDevices,
    type CallSessionEventHandlers,
    type MediaDevice,
    type MediaDeviceQuery,
    type SelectedMediaDevices,
} from '@yandex-360/telemost-sdk/device-manager';
import type { Event } from '@yandex-int/messenger.utils';
import type { Session } from '@yandex-video-platform/goloom-sdk';
import { SessionEvent } from '@yandex-video-platform/goloom-sdk';

import type { GoloomSDK } from '../../../goloom-sdk/goloom-sdk';
import {
    mapActiveDevicesChangedFromGoloom,
    mapActiveDevicesFromGoloom,
    mapDeviceChangedFromGoloom,
    mapDeviceToGoloom,
    mapGoloomDeviceFromGoloom,
    mapSelectedDevicesFromGoloom,
    mapSoundProcessorToGoloom,
    mapVideoProcessorToGoloom,
} from './goloom-type-mappers';

type WrappedCallHandlers = {
    onAudioStatusChanged: (event: SessionEvent['audioStatusChanged']) => void | Promise<void>;
    onVideoStatusChanged: (event: SessionEvent['videoStatusChanged']) => void | Promise<void>;
    onActiveDevicesChanged: (event: SessionEvent['activeDevicesChanged']) => void | Promise<void>;
    onDeviceChanged: (event: SessionEvent['deviceChanged']) => void | Promise<void>;
};

export class GoloomCallSession extends CallMediaSession {
    private readonly wrappedHandlers = new Map<CallSessionEventHandlers, WrappedCallHandlers>();
    private readonly pendingHandlers = new Set<CallSessionEventHandlers>();

    constructor(private readonly goloomSdk: GoloomSDK) {
        super();
        this.goloomSdk.onInit.addListener(this.flushPendingSubscriptions);
        this.goloomSdk.onDestroy.addListener(this.handleSessionDestroy);
    }

    private get session() {
        return this.goloomSdk.session;
    }

    isInitialized(): boolean {
        return Boolean(this.session);
    }

    get onInit(): Event<true> {
        return this.goloomSdk.onInit;
    }

    get onJoin(): Event {
        return this.goloomSdk.onJoin;
    }

    get onDestroy(): Event<true> {
        return this.goloomSdk.onDestroy;
    }

    get activeDevices(): ActiveMediaDevices {
        return this.session
            ? mapActiveDevicesFromGoloom(this.session.activeDevices)
            : { audioInput: null, videoInput: null, audioOutput: null };
    }

    get selectedDevices(): SelectedMediaDevices | undefined {
        return this.session ? mapSelectedDevicesFromGoloom(this.session.selectedDevices) : undefined;
    }

    get audioStatus(): boolean | undefined {
        return this.session?.audioStatus;
    }

    get videoStatus(): boolean | undefined {
        return this.session?.videoStatus;
    }

    getAudioInputActiveDevice(): MediaDevice | null | undefined {
        const device = this.session?.activeDevices.audioInput;
        return device ? mapGoloomDeviceFromGoloom(device) : device;
    }

    getVideoInputActiveDevice(): MediaDevice | null | undefined {
        const device = this.session?.activeDevices.videoInput;
        return device ? mapGoloomDeviceFromGoloom(device) : device;
    }

    async selectDevice(device: MediaDeviceQuery): Promise<void> {
        if (!this.session) {
            throw new Error('Call session is not initialized');
        }

        await this.session.selectDevice(mapDeviceToGoloom(device));
    }

    async applySelectedDevices(): Promise<void> {
        if (!this.session) {
            throw new Error('Call session is not initialized');
        }

        await this.session.applySelectedDevices();
    }

    async setAudioStatus(status: boolean): Promise<unknown> {
        if (!this.session) {
            throw new Error('Call session is not initialized');
        }

        return this.session.setAudioStatus(status);
    }

    async setVideoStatus(status: boolean): Promise<unknown> {
        if (!this.session) {
            throw new Error('Call session is not initialized');
        }

        return this.session.setVideoStatus(status);
    }

    async setSoundProcessorOptions(options: Record<string, unknown>): Promise<void> {
        await this.goloomSdk.setSoundProcessorOptions(mapSoundProcessorToGoloom(options));
    }

    async setVideoProcessorOptions(options: Record<string, unknown>): Promise<void> {
        await this.goloomSdk.setVideoProcessorOptions(mapVideoProcessorToGoloom(options));
    }

    subscribe(handlers: CallSessionEventHandlers): void {
        if (this.wrappedHandlers.has(handlers) || this.pendingHandlers.has(handlers)) {
            return;
        }

        if (this.session) {
            this.attachHandlers(handlers);
            return;
        }

        this.pendingHandlers.add(handlers);
    }

    unsubscribe(handlers: CallSessionEventHandlers): void {
        this.pendingHandlers.delete(handlers);

        const wrapped = this.wrappedHandlers.get(handlers);

        if (!wrapped) {
            return;
        }

        this.detachHandlers(wrapped);
        this.wrappedHandlers.delete(handlers);
    }

    private flushPendingSubscriptions = (): void => {
        if (!this.session) {
            return;
        }

        for (const handlers of this.pendingHandlers) {
            if (!this.wrappedHandlers.has(handlers)) {
                this.attachHandlers(handlers);
            }
        }

        this.pendingHandlers.clear();
    };

    private handleSessionDestroy = (): void => {
        for (const [handlers, wrapped] of this.wrappedHandlers.entries()) {
            this.detachHandlers(wrapped);
            this.pendingHandlers.add(handlers);
        }

        this.wrappedHandlers.clear();
    };

    private attachHandlers(handlers: CallSessionEventHandlers): void {
        const session = this.session;

        if (!session || this.wrappedHandlers.has(handlers)) {
            return;
        }

        const wrapped = this.createWrappedHandlers(handlers);

        this.wrappedHandlers.set(handlers, wrapped);
        this.addWrappedListeners(session, wrapped);
    }

    private createWrappedHandlers(handlers: CallSessionEventHandlers): WrappedCallHandlers {
        return {
            onAudioStatusChanged: (event) => handlers.onAudioStatusChanged({ status: event.status }),
            onVideoStatusChanged: (event) => handlers.onVideoStatusChanged({ status: event.status }),
            onActiveDevicesChanged: (event) =>
                handlers.onActiveDevicesChanged(mapActiveDevicesChangedFromGoloom(event)),
            onDeviceChanged: (event) => handlers.onDeviceChanged(mapDeviceChangedFromGoloom(event)),
        };
    }

    private addWrappedListeners(session: Session, wrapped: WrappedCallHandlers): void {
        session.addAsyncEventListener('audioStatusChanged', wrapped.onAudioStatusChanged);
        session.addAsyncEventListener('videoStatusChanged', wrapped.onVideoStatusChanged);
        session.addAsyncEventListener('activeDevicesChanged', wrapped.onActiveDevicesChanged);
        session.addAsyncEventListener('deviceChanged', wrapped.onDeviceChanged);
    }

    private detachHandlers(wrapped: WrappedCallHandlers): void {
        const session = this.session;

        if (!session) {
            return;
        }

        session.removeAsyncEventListener('audioStatusChanged', wrapped.onAudioStatusChanged);
        session.removeAsyncEventListener('videoStatusChanged', wrapped.onVideoStatusChanged);
        session.removeAsyncEventListener('activeDevicesChanged', wrapped.onActiveDevicesChanged);
        session.removeAsyncEventListener('deviceChanged', wrapped.onDeviceChanged);
    }
}
